<template>
  <div class="sp-broadcast-message" :class="classModifiers">
    <div class="sp-broadcast-message-container">
      <figure v-show="hasIcon" class="sp-broadcast-message__icon">
        <sp-icon v-if="icon" :size="props.iconSize" :name="icon" :fill-color="iconFillColor"></sp-icon>
        <slot v-else ref="iconContainer" name="icon" />
      </figure>

      <div class="sp-broadcast-message__content">
        <component
          :is="titleTag"
          v-show="hasTitle"
          class="sp-broadcast-message__title"
          v-bind="titleToggleAttributes"
          @click="toggleOnClick"
          @keydown="toggleOnActivationKey"
        >
          <slot ref="titleSlot" name="title" />
        </component>
        <div class="sp-broadcast-message__text">
          <slot />
        </div>
      </div>
    </div>

    <div class="sp-broadcast-message__actions">
      <slot name="action">
        <sp-broadcast-action v-if="action" :href="action.link">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-html="action.content"></span>
        </sp-broadcast-action>
      </slot>
      <button
        v-if="isDismissable"
        type="button"
        class="sp-broadcast-message__actions-dismiss"
        :aria-label="dismissLabel"
        @click="dismissBroadcast"
      >
        x
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { crossedInteractiveContent } from "#ce/utils/events";
import { toBoolean } from "#ce/utils/props";
import { t } from "./i18n.js";
import { props as broadcastProps } from "./props.js";

const props = defineProps(broadcastProps);

const { gtModifiers } = useBreakpoints();

const iconContainer = useTemplateRef("iconContainer");
const hasIcon = computed(
  () => props.icon !== "none-filled" && (props.icon || iconContainer.value?.assignedNodes?.().length > 0),
);
const titleSlot = useTemplateRef("titleSlot");
const hasTitle = computed(() => titleSlot.value?.assignedNodes?.().length > 0);

const isCollapsable = computed(() => toBoolean(props.collapsable));
const isTextCollapsed = ref(isCollapsable.value);

const ACTIVATION_KEYS = ["Enter", " "];

function collapseText() {
  if (isCollapsable.value) {
    isTextCollapsed.value = !isTextCollapsed.value;
  }
}

// The title is WYSIWYG content, so a link or button inside it keeps its own click. The walk stops
// at the title itself, so a `button` title tag toggles.
function toggleOnClick(event) {
  if (crossedInteractiveContent(event, event.currentTarget)) {
    return;
  }

  collapseText();
}

// Enter/Space are claimed only while the title is the toggle, since `titleTag` accepts elements
// that activate on those keys themselves. The default is prevented on every press so a held Space
// never scrolls the page; the state changes once per press, not per auto-repeat.
function toggleOnActivationKey(event) {
  if (!isCollapsable.value || !ACTIVATION_KEYS.includes(event.key)) {
    return;
  }

  if (crossedInteractiveContent(event, event.currentTarget)) {
    return;
  }

  event.preventDefault();

  if (!event.repeat) {
    collapseText();
  }
}

// The title is the toggle when the broadcast collapses, so it carries button semantics.
const titleToggleAttributes = computed(() => {
  if (!isCollapsable.value) {
    return {};
  }

  return { role: "button", tabindex: 0, "aria-expanded": String(!isTextCollapsed.value) };
});

const isDismissable = computed(() => toBoolean(props.dismissable));
const isDismissed = ref(false);
const dismissLabel = computed(() => t("dismiss"));

function dismissBroadcast() {
  isDismissed.value = true;
}

const classModifiers = computed(() => [
  ...gtModifiers.value,
  {
    [`--variant-${props.variant}`]: props.variant,
    [`--level-${props.level}`]: props.level,
    "--is-collapsable": isCollapsable.value,
    "--is-expanded": !isTextCollapsed.value,
    "--has-icon": hasIcon.value,
    "--is-dismissable": isDismissable.value,
    "--is-dismissed": isDismissed.value,
    "--content-inline": toBoolean(props.inline),
  },
]);
const iconFillColor = computed(() => `var(--sp-comp-broadcast-message-level-${props.level}-icon-color)`);

/**
 * Sets the fill color of the icon.
 *
 * This is needed because the icon can either be a string or a slot.
 * If it is a slot, the fill color needs to be set on the icon itself using the `fill-color` attribute.
 * If it is a slot a reference to the slot is stored in the `iconContainer` ref.
 */
function setIconFillColor() {
  setIconAttribute("fill-color", iconFillColor.value);
}

function setIconAttribute(name, value) {
  if (!iconContainer.value) {
    return;
  }
  // find the sp-icon element in the slot
  const icon = iconContainer.value
    .assignedNodes()
    .filter(({ tagName }) => tagName === "SP-ICON")
    ?.at(0);

  icon?.setAttribute(name, value);
}

watch(iconFillColor, () => {
  setIconFillColor();
});

onMounted(() => {
  setIconFillColor();
});
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-broadcast-message {
  --broadcast-text-color: inherit;
  --padding-block: var(--sp-comp-broadcast-message-padding-block, 1rem);
  --padding-inline: var(--sp-comp-broadcast-message-padding-inline, 1rem);
  --padding: var(--padding-block) var(--padding-inline);
  --grid-gap: var(--sp-comp-broadcast-message-grid-gap, 0.5rem);
  --actions-dismiss-size: var(--sp-comp-broadcast-message-actions-dismiss-size, 2rem);

  display: grid;
  gap: var(--grid-gap);
  grid-template-columns: 1fr auto; // content actions
  padding: var(--padding);
  position: relative;
  z-index: var(--sp-comp-broadcast-message-z-index, 1);

  &.--is-dismissed {
    display: none;
  }

  &.--variant-default {
    padding: var(--padding);
  }

  &.--variant-inline {
    border-radius: var(--sp-comp-broadcast-message-variant-inline-mobile-border-radius, 0);
    --padding-block: var(--sp-comp-broadcast-message-variant-inline-padding-block, 1rem);
    --padding-inline: var(--sp-comp-broadcast-message-variant-inline-padding-inline, 1rem);

    &.--gt-sm-screen {
      border-radius: var(--sp-comp-broadcast-message-variant-inline-border-radius, 0);
    }

    .sp-broadcast-message-container {
      padding-inline: var(--sp-comp-broadcast-message-variant-inline-container-padding-inline, 0);
    }
  }

  &.--variant-full {
    --padding-block: var(--sp-comp-broadcast-message-variant-full-padding-block, 0.625rem);
    --padding-inline: var(--sp-comp-broadcast-message-variant-full-padding-inline, 0.625rem);
    --container-max-width: var(--sp-comp-broadcast-message-variant-full-container-max-width, 1170px);
    margin: 0;

    .sp-broadcast-message-container {
      justify-self: center;
      max-width: var(--container-max-width);
      padding-inline: var(--sp-comp-broadcast-message-variant-full-container-padding-inline, 10px);
    }

    &.--gt-xl-screen {
      --container-max-width: var(--sp-comp-broadcast-message-variant-full-container-max-width-xl, 1280px);
    }
  }
  &.--content-inline:not(.--is-collapsable) {
    .sp-broadcast-message__title,
    .sp-broadcast-message__text {
      display: inline !important;
    }

    .sp-broadcast-message__title {
      &:after {
        content: " ";
        display: inline;
      }
      margin-bottom: 0;
    }
  }

  @each $level in ("disclosure", "info", "warning", "alert") {
    &.--level-#{$level} {
      --broadcast-text-color: var(--sp-comp-broadcast-message-level-#{$level}-text-color);
      background-color: var(--sp-comp-broadcast-message-level-#{$level}-background-color);
      color: var(--broadcast-text-color);
    }
  }

  // Disclosure has no background color, and should not have any padding.
  &.--level-disclosure {
    --padding-block: 0;
    --padding-inline: 0;
  }

  &.--is-collapsable {
    // Room for the chevron plus a gap so it does not sit against the last word.
    --chevron-space: var(--sp-comp-broadcast-message-title-chevron-space, 1.75rem);

    .sp-broadcast-message__title,
    .sp-broadcast-message__text {
      padding-right: var(--chevron-space);
    }

    .sp-broadcast-message__title {
      display: block;
      position: relative;
      cursor: pointer;
      margin-bottom: 0;
      &::after {
        position: absolute;
        top: 0.475em;
        right: 0.3rem;
        border-style: solid;
        border-width: 0.3em 0.3em 0;
        content: "";
        transition: transform 0.2s ease-in-out;
        color: var(--broadcast-text-color, #000);
        border-color: var(--broadcast-text-color, #000) transparent transparent;
      }
    }

    .sp-broadcast-message__content .sp-broadcast-message__text {
      display: none;
    }

    &.--is-expanded {
      .sp-broadcast-message__title::after {
        transform: rotate(180deg);
      }

      .sp-broadcast-message__content .sp-broadcast-message__text {
        display: block;
        margin-top: 1rem;
      }
    }

    .sp-broadcast-message-container {
      width: 100%;
    }
  }

  &:not(.--has-icon) {
    .sp-broadcast-message__icon {
      width: 0;
      height: 0;
    }
  }
}

.sp-broadcast-message-container {
  box-sizing: border-box;
  display: grid;
  gap: var(--sp-comp-broadcast-message-grid-gap, 0.5rem);
  grid-template-columns: auto 1fr; // icon content
  align-items: center;
  justify-self: var(--sp-comp-broadcast-message-container-justify-self, auto);
  max-width: var(--sp-comp-broadcast-message-container-max-width, none);
  padding-inline: var(--sp-comp-broadcast-message-container-padding-inline, 0);
}

.sp-broadcast-message__icon {
  sp-icon {
    --size: var(--sp-comp-broadcast-message-icon-size, var(--sp-sys-icon-size-medium, 2rem));
  }
  margin: 0;
  align-self: baseline;
}

.sp-broadcast-message__content {
  color: var(--broadcast-text-color);
  line-height: var(--sp-comp-broadcast-message-line-height, 1.2);
  padding-top: var(--sp-comp-broadcast-message-content-padding-top, 0.5rem);
  padding-bottom: var(--sp-comp-broadcast-message-content-padding-bottom, 0.5rem);
}

.sp-broadcast-message__title {
  display: block;
  font-family: var(--sp-comp-broadcast-message-title-typeface, sans-serif);
  font-size: var(--sp-comp-broadcast-message-title-size, 1rem);
  font-weight: var(--sp-comp-broadcast-message-title-weight, bold);
  line-height: var(--sp-comp-broadcast-message-line-height, 1.2);
  margin: 0;
  margin-bottom: var(--sp-comp-broadcast-message-title-margin-bottom, 1rem);
}

.sp-broadcast-message__text {
  display: block;
  font-family: var(--sp-comp-broadcast-message-text-typeface, sans-serif);
  font-size: var(--sp-comp-broadcast-message-text-size, 1rem);
  font-weight: var(--sp-comp-broadcast-message-text-weight, normal);
  line-height: var(--sp-comp-broadcast-message-line-height, 1.2);
}

.sp-broadcast-message__actions {
  display: flex;
  gap: var(--grid-gap);
}

.sp-broadcast-message__actions-dismiss {
  // TODO: replace with icon
  appearance: none;
  background: none;
  border: 0;
  font: inherit;
  padding: 0;
  cursor: pointer;
  display: flex;
  font-family: sans-serif;
  align-items: center;
  justify-content: center;
  color: var(--sp-comp-broadcast-message-actions-dismiss-color, #000);
  align-self: flex-start;
  height: var(--actions-dismiss-size);
  width: var(--actions-dismiss-size);
}
</style>
